import { CourseLocation } from '../models/enums/course-location.enum';
import { CourseStatus } from '../models/enums/course-status.enum';
import { CourseType } from '../models/enums/course-type.enum';
import { ContentType } from '../models/enums/content-type.enum';
import { Class, Content, ContentPayload } from '../models/interfaces/course-content.interface';
import { ModulePayload } from '../models/interfaces/module.interface';
import { Course } from '../models/interfaces/course.interface';
import { Tag } from '../models/interfaces/tag.interface';
import { Evaluation } from '../models/interfaces/course-evaluation.interface';
import { CourseListingMode } from '../models/enums/course-listing-mode.enum';
import { CourseFilterParams } from '../models/interfaces/course-filter-value.interface';
import {
  CourseContentWritePayload,
  CourseModuleWritePayload,
  CoursePayload,
  CourseRelatedResources,
  CourseSearchPayload,
} from '../types/payloads/course.payload';
import { ClassFormValue } from '../models/interfaces/forms/course-form.interface';

/**
 * Formato único de duração no wizard: horas decimais com ponto ou vírgula
 * (ex.: "12", "0.5", "4.5"). Não é enviado no CoursePayload.
 */
export const COURSE_DURATION_HOURS_FORMAT = 'decimal-hours';

const COURSE_DESCRIPTION_MAX_LENGTH = 255;

type ApiCourseContent = {
  id?: string;
  moduleId?: string;
  title?: string;
  order?: number;
  type?: ContentType;
  htmlContent?: string | null;
  urlFolder?: string | null;
  manifestPath?: string | null;
  videoUrl?: string | null;
  urlPath?: string | null;
  entrypoint?: string | null;
  questions?: Array<{
    id?: string;
    question?: string;
    text?: string;
    imageUrl?: string;
    options?: Array<{ id?: string; text?: string; isCorrect?: boolean }>;
  }>;
  classes?: Class[];
  evaluations?: Evaluation[];
  createdAt?: Date;
  updatedAt?: Date;
  isActive?: boolean;
};

export function resolveCourseDescription(detailedDescription?: string, shortDescription?: string): string | undefined {
  const detailed = detailedDescription?.trim();
  if (detailed) {
    return detailed.slice(0, COURSE_DESCRIPTION_MAX_LENGTH);
  }

  const short = shortDescription?.trim();
  return short ? short.slice(0, COURSE_DESCRIPTION_MAX_LENGTH) : undefined;
}

export function parseDurationHours(durationHours?: string): number | undefined {
  if (!durationHours?.trim()) {
    return undefined;
  }

  const parsed = Number.parseFloat(durationHours.trim().replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function durationHoursToMinutes(durationHours?: string): number | undefined {
  const hours = parseDurationHours(durationHours);
  return hours == null ? undefined : Math.round(hours * 60);
}

export function minutesToDurationHoursInput(durationMinutes?: number): string {
  if (durationMinutes == null) {
    return '';
  }

  const hours = durationMinutes / 60;
  return Number.isInteger(hours) ? String(hours) : String(Number(hours.toFixed(2)));
}

export function toCourseApiStatus(status: CourseStatus): 'draft' | 'published' | 'inactive' {
  if (status === CourseStatus.ACTIVE) {
    return 'published';
  }

  if (status === CourseStatus.INACTIVE) {
    return 'inactive';
  }

  return 'draft';
}

export function fromCourseApiStatus(status?: string | CourseStatus): CourseStatus {
  if (status === 'published' || status === CourseStatus.ACTIVE) {
    return CourseStatus.ACTIVE;
  }

  if (status === 'inactive' || status === CourseStatus.INACTIVE) {
    return CourseStatus.INACTIVE;
  }

  if (status === 'deleted' || status === CourseStatus.DELETED) {
    return CourseStatus.DELETED;
  }

  return CourseStatus.DRAFT;
}

export function toCourseApiPayload(input: {
  title: string;
  categoryId: string | null;
  detailedDescription?: string;
  shortDescription?: string;
  coverImageUrl?: string;
  type?: CourseType | null;
  location?: CourseLocation | null;
  studentsIds?: string[];
  status?: CourseStatus;
  tags?: Tag[];
  durationHours?: string;
  modules?: CourseModuleWritePayload[];
}): CoursePayload {
  if (!input.categoryId) {
    throw new Error('A categoria do curso é obrigatória.');
  }

  return {
    title: input.title.trim(),
    categoryId: input.categoryId,
    description: resolveCourseDescription(input.detailedDescription, input.shortDescription),
    shortDescription: input.shortDescription?.trim() || '',
    tagsIds: toCourseTagsIds(input.tags),
    durationMinutes: durationHoursToMinutes(input.durationHours) ?? null,
    coverImageUrl: input.coverImageUrl?.trim() || undefined,
    type: input.type ?? CourseType.COURSE,
    location: input.location ?? CourseLocation.ONLINE,
    ...(input.studentsIds?.length ? { studentsIds: input.studentsIds } : {}),
    ...(input.status ? { status: toCourseApiStatus(input.status) } : {}),
    modules: input.modules ?? [],
  };
}

export function toCourseTagsIds(tags?: Tag[]): string[] {
  return [...new Set((tags ?? []).map((tag) => tag.id).filter((id) => !!id))];
}

export function toCourseRelatedResources(input: {
  durationHours?: string;
  tags: Tag[];
  status: CourseStatus;
  modules: ModulePayload[];
  contents: ContentPayload[];
}): CourseRelatedResources {
  return {
    durationHours: input.durationHours?.trim() || undefined,
    tags: input.tags,
    status: input.status,
    modules: input.modules,
    contents: input.contents,
  };
}

export function toCourseSearchPayload(params: CourseFilterParams): CourseSearchPayload {
  const payload: CourseSearchPayload = {
    page: params.page || 1,
    limit: params.limit || 10,
    listingMode: params.listingMode ?? CourseListingMode.PUBLISHED,
  };

  const keyword = params.keyword?.trim();
  if (keyword) {
    payload.keyword = keyword;
  }

  if (params.sort?.field) {
    payload.sort = `${params.sort.field},${params.sort.order ?? 'ASC'}`;
  }

  if (params.categories?.length) {
    payload.categoryIds = params.categories.map((category) => category.id);
  }

  if (params.status && params.status !== 'all') {
    payload.status = params.status === CourseStatus.DELETED ? 'deleted' : toCourseApiStatus(params.status);
  }

  return payload;
}

export function toClassWritePayload(
  courseClass: ClassFormValue,
  persistedIds: Set<string>,
): CourseContentWritePayload {
  const payload: CourseContentWritePayload = {
    ...(persistedIds.has(courseClass.id) ? { id: courseClass.id } : {}),
    title: courseClass.name.trim(),
    order: courseClass.order,
    type: courseClass.type!,
  };

  if (courseClass.type === ContentType.RICH_TEXT && courseClass.editorContent) {
    payload.htmlContent = courseClass.editorContent;
  }

  if (courseClass.type === ContentType.VIDEO) {
    const videoSource = courseClass.videoFileName.trim();

    if (videoSource) {
      payload.urlFolder = videoSource;
      payload.manifestPath = videoSource;
    }
  }

  if (courseClass.type === ContentType.YOUTUBE && courseClass.youtubeUrl.trim()) {
    payload.videoUrl = courseClass.youtubeUrl.trim();
  }

  if (courseClass.type === ContentType.VIMEO && courseClass.vimeoUrl.trim()) {
    payload.videoUrl = courseClass.vimeoUrl.trim();
  }

  if (courseClass.type === ContentType.HTML) {
    assignHtmlWriteFields(payload, courseClass.webPageFileName.trim());
  }

  return payload;
}

function assignHtmlWriteFields(payload: CourseContentWritePayload, webPageSource: string): void {
  if (!webPageSource) {
    return;
  }

  if (isHttpUrl(webPageSource)) {
    payload.urlPath = webPageSource;
    payload.entrypoint = fileNameFromUrl(webPageSource);
    return;
  }

  payload.entrypoint = webPageSource;
}

export function normalizeCourse(course: Course): Course {
  return {
    ...course,
    status: fromCourseApiStatus(course.status as string),
    isActive: fromCourseApiStatus(course.status as string) === CourseStatus.ACTIVE,
    shortDescription: course.shortDescription?.trim() || undefined,
    contents: groupCourseContents(course),
  };
}

export function groupCourseContents(course: Course): Content[] {
  const contents = (course.contents ?? []) as ApiCourseContent[];

  if (!contents.length) {
    return [];
  }

  if (contents.some((item) => Array.isArray(item.classes) || Array.isArray(item.evaluations))) {
    return contents as Content[];
  }

  const modules = [...(course.modules ?? [])].sort((left, right) => left.order - right.order);

  return modules.map((module, moduleOrder) => {
    const moduleContents = contents
      .filter((item) => item.moduleId === module.id)
      .sort((left, right) => (left.order ?? 0) - (right.order ?? 0));

    return {
      id: module.id,
      createdAt: module.createdAt,
      updatedAt: module.updatedAt,
      isActive: true,
      moduleId: module.id,
      moduleOrder: module.order ?? moduleOrder,
      classes: moduleContents
        .filter((item) => item.type !== ContentType.ACTIVITY)
        .map((item) => mapApiContentToClass(item)),
      evaluations: moduleContents
        .filter((item) => item.type === ContentType.ACTIVITY)
        .map((item, order) => mapApiContentToEvaluation(item, order)),
    };
  });
}

export function collectPersistedContentIds(course: Course): Set<string> {
  const ids = new Set<string>();

  (course.modules ?? []).forEach((module) => addPersistedId(ids, module.id));
  (course.contents ?? []).forEach((content) => collectContentIds(ids, content));

  return ids;
}

function collectContentIds(ids: Set<string>, content: Content): void {
  addPersistedId(ids, content.id);
  content.classes?.forEach((item) => addPersistedId(ids, item.id));
  content.evaluations?.forEach((evaluation) => collectEvaluationIds(ids, evaluation));
}

function collectEvaluationIds(ids: Set<string>, evaluation: Evaluation): void {
  addPersistedId(ids, evaluation.id);
  evaluation.questions?.forEach((question) => collectQuestionIds(ids, question));
}

function collectQuestionIds(ids: Set<string>, question: Evaluation['questions'][number]): void {
  addPersistedId(ids, question.id);
  question.options?.forEach((option) => addPersistedId(ids, option.id));
}

function addPersistedId(ids: Set<string>, id?: string): void {
  if (id) {
    ids.add(id);
  }
}

function mapApiContentToClass(item: ApiCourseContent): Class {
  return {
    id: item.id ?? crypto.randomUUID(),
    createdAt: item.createdAt ?? new Date(),
    updatedAt: item.updatedAt ?? new Date(),
    isActive: item.isActive ?? true,
    order: item.order ?? 0,
    title: item.title ?? '',
    type: item.type ?? ContentType.RICH_TEXT,
    editorContent: item.htmlContent ?? undefined,
    videoFileName: item.urlFolder ?? item.manifestPath ?? undefined,
    youtubeUrl: item.type === ContentType.YOUTUBE ? item.videoUrl ?? undefined : undefined,
    vimeoUrl: item.type === ContentType.VIMEO ? item.videoUrl ?? undefined : undefined,
    webPageFileName: storedWebPageSource(item.urlPath, item.entrypoint),
  };
}

function storedWebPageSource(urlPath?: string | null, entrypoint?: string | null): string | undefined {
  const path = urlPath?.trim() ?? '';
  const entry = entrypoint?.trim() ?? '';

  if (isHttpUrl(path)) {
    return path;
  }

  if (isHttpUrl(entry)) {
    return entry;
  }

  return entry || path || undefined;
}

function isHttpUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

function fileNameFromUrl(value: string): string {
  try {
    const pathname = new URL(value).pathname;
    const fileName = pathname.split('/').findLast((part) => part.length > 0);
    return fileName?.includes('.') ? fileName : 'index.html';
  } catch {
    return 'index.html';
  }
}

function mapApiContentToEvaluation(item: ApiCourseContent, order: number): Evaluation {
  return {
    id: item.id ?? crypto.randomUUID(),
    createdAt: item.createdAt ?? new Date(),
    updatedAt: item.updatedAt ?? new Date(),
    isActive: item.isActive ?? true,
    order: item.order ?? order,
    questions: (item.questions ?? []).map((question, questionOrder) => ({
      id: question.id ?? crypto.randomUUID(),
      createdAt: item.createdAt ?? new Date(),
      updatedAt: item.updatedAt ?? new Date(),
      isActive: true,
      order: questionOrder,
      text: question.text ?? question.question ?? '',
      imageUrl: question.imageUrl,
      options: (question.options ?? []).map((option, optionOrder) => ({
        id: option.id ?? crypto.randomUUID(),
        createdAt: item.createdAt ?? new Date(),
        updatedAt: item.updatedAt ?? new Date(),
        isActive: true,
        order: optionOrder,
        text: option.text ?? '',
        isCorrect: option.isCorrect === true,
      })),
    })),
  };
}

export type { CourseModuleWritePayload, CourseContentWritePayload };
